import React, { useState, useEffect } from 'react';
import 'moment/locale/pt';

import { Box, Button, Modal, Tooltip, Typography } from '@mui/material';
import { Download, Key } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomizedTables from '../../../components/Table';
import FixedContainer from '../../../components/Container';
import LoadingComponent from '../../../components/LoadingComponent';
import { GetFetch, PostFetch, urls } from '../../../services/apiBase';
import { useAppStatus } from '../../../hooks/app';
import { useAuth } from "../../../hooks/auth";
import { enqueueSnackbar } from 'notistack';
import ChooseSignerModal from './components/chooseSigner';

const StatusCPRSignature = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
}

const PendentesAssinatura = () => {
  const { user } = useAuth();
  const [cprs, setCprs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [toggleSignerModal, setToggleSignerModal] = useState(false);
  const [selectedSignerToken, setSelectedSignerToken] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const statusSignature = searchParams.get('status') ?? StatusCPRSignature.PENDING;
  const title = statusSignature === StatusCPRSignature.PENDING ? 'Pendentes de Assinatura' : 'Assinadas';
  
  const navigate = useNavigate()
  const { getCprSummarization } = useAppStatus();

  const COLUMNSTABLE = [
    {
      headerName: 'N. da CPR',
      type: 'default',
      value: {
        first: {
          value: 'cpr_number'
        }
      }
    },
    {
      headerName: 'Data de geração',
      type: 'date',
      value: {
        first: {
          value: 'date_start'
        }
      }
    },
    {
      headerName: 'Data de ativação',
      type: 'date',
      value: {
        first: {
          value: 'date_signed'
        }
      }
    },
    {
      headerName: 'Data final',
      type: 'date',
      value: {
        first: {
          value: 'date_end'
        }
      }
    },
    {
      headerName: 'Valor investido',
      type: 'money',
      value: {
        first: {
          value: 'amount_cpr'
        }
      }
    },
    {
      headerName: 'Taxa',
      type: 'percent',
      value: {
        first: {
          value: 'rate_cpr'
        }
      }
    },
    {
      headerName: 'Status',
      type: 'default',
      value: {
        first: {
          value: 'status'
        }
      }
    },
    {
      headerName: 'Tipo',
      type: 'default',
      value: {
        first: {
          value: 'type'
        }
      }
    },
    {
      headerName: '1º Signatário',
      type: 'default',
      value: {
        first: {
          value: 'signature_1'
        }
      }
    },
    {
      headerName: '2º Signatário',
      type: 'default',
      value: {
        first: {
          value: 'signature_2'
        }
      }
    },
    {
      headerName: 'Prazo para assinatura',
      type: 'default',
      value: {
        first: {
          parent: ['statusExpirationSignature'],
          value: 'label'
        }
      }
    }
  ]

  const handleOpenDetails = (cpr) => {
    return navigate('/cprf/details', { state: { cpr: cpr } })
  }

  const columnStyle = (column, row) => {
    if (row.cpr_signature_flow.completed_at) {
      return {};
    }

    if (column.headerName === '1º Signatário') {
      return row.signature_1
        ? { backgroundColor: 'green', color: 'white' }
        : { backgroundColor: 'red', color: 'white' };
    }
    if (column.headerName === '2º Signatário') {
      return row.signature_2
        ? { backgroundColor: 'green', color: 'white' }
        : { backgroundColor: 'red', color: 'white' };
    }

    if (column.headerName === 'Prazo para assinatura') {
      const colors = {
        'DENTRO_PRAZO': { backgroundColor: 'green', color: 'white' },
        'EM_ALERTA': { backgroundColor: 'yellow', color: 'black' },
        'ATRASADA': { backgroundColor: 'red', color: 'white' },
      }
      return colors[row.statusExpirationSignature.name];
    }

    return {};
  }
  
  const handleChangeFilter = () => {
    const newStatus = statusSignature === StatusCPRSignature.PENDING 
      ? StatusCPRSignature.COMPLETED
      : StatusCPRSignature.PENDING;
    searchParams.set('status', newStatus);
    setSearchParams(searchParams);
  }
  
  const handleDownloadDocument = async (row) => {
    const isCompleted = !!row.cpr_signature_flow.completed_at;
    const responseSignedDocument = await PostFetch(`/financial/cprBase64`, {
      cpr_number: row.cpr_number,
      personne_id: row.personne_id,
      type_email: isCompleted ? 'signed' : 'customer',
    });

    if (typeof responseSignedDocument.data === 'string') {
      window.location.href = responseSignedDocument.data;
    } else {
      window.open(`data:application/pdf;base64,${responseSignedDocument.data.content_B64}`, '_blank')
    }
  }

  const errorMessage = () => {
    return (
      <Box sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center'
      }}>
        <Typography variant='h6' sx={{ fontWeight: 700, fontSize: '24px', paddingBottom: 2, color: 'red' }}>
          Ocorreu um erro ao buscar os cupons
        </Typography>
      </Box>
    )
  }

  const tableMoreActions = (row) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 2, gap: 2, cursor: 'pointer' }}>
        {!!row.cpr_signature_flow.completed_at && (
          <Tooltip title="Download">
          <Box onClick={() => handleDownloadDocument(row)}>
            <Download color="primary" />
          </Box>
        </Tooltip>
        )}
        {!row.cpr_signature_flow.completed_at && row.signer_token && (
        <Tooltip title="Assinar">
          <Box onClick={() => {
            setSelectedSignerToken(row.signer_token);
            setToggleSignerModal(true);
          }}>
            <Key color="primary" />
          </Box>
        </Tooltip>
        )}
      </Box>
    )
  }

  const tableComponent = () => {
    const isSignaturePending = statusSignature === StatusCPRSignature.PENDING;
    const columns = isSignaturePending ? COLUMNSTABLE : COLUMNSTABLE.filter(col => col.headerName !== 'Prazo para assinatura')
    return (
      <Box>
        <CustomizedTables
          data={cprs}
          columns={columns}
          options
          action={handleOpenDetails}
          columnStyle={columnStyle}
          moreActions={tableMoreActions}
        />
      </Box>
    )
  }

  useEffect(() => {
    async function getCPR() {
      setLoading(true)
      try {
        const response = await GetFetch(urls.GET_CPR_PENDENTES_ASSINATURA, {
          params: {
            statusSignature,
          }
        });
  
        if (!response.data.error) {
          const ordered = response.data.sort((a, b) => a.cpr_number < b.cpr_number ? 1 : -1);
          setCprs(ordered);
        } else {
          setCprs(`No momento não há CPRS ${title}`)
        }
        setError(false)
  
      } catch (error) {
        console.log(error)
        setCprs(`Ocorreu um erro ao buscar as CPRS ${title}. Por favor, tente mais tarde`)
        setError(true)
      } finally {
        setLoading(false);
      }
    }
    
    getCprSummarization();
    getCPR();
  }, [statusSignature]);

  return (
    <>
      <FixedContainer>
        <Box sx={{
          width: '100%',
          height: '100%',
          backgroundColor: '#F9FBFD',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          boxShadow: 3,
          borderRadius: '10px',
          padding: 2,
        }}>
          {loading ? <LoadingComponent /> :
            error ? errorMessage() :
            cprs === `No momento não há CPRS ${title}` ? 
              <Box sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center'
              }}>
                <Typography variant='h6' sx={{ fontWeight: 700, fontSize: '18px', paddingBottom: 2 }}>
                  No momento não há CPRS pendentes de assinatura
                </Typography>
              </Box>
              :
              <>
                <Typography variant='h6' sx={{ fontWeight: 700, fontSize: '18px', paddingBottom: 1 }}>
                  CPRF's/Pendentes de Assinatura
                </Typography>

                <Box sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'end'
                }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleChangeFilter}
                    sx={{ marginBottom: 2 }}
                  >
                    Filtrar por CPRs {statusSignature === StatusCPRSignature.PENDING ? 'Assinadas' : 'Pendentes de Assinatura'}
                  </Button>
                </Box>
                {tableComponent()}
              </>
          }
        </Box>
      </FixedContainer>

      <Modal
        open={toggleSignerModal}
        onClose={() => setToggleSignerModal(false)}
      >
        <ChooseSignerModal
          signerToken={selectedSignerToken}
          onClose={() => setToggleSignerModal(false)}
        />
      </Modal>
    </>
  );
}

export default PendentesAssinatura;
